import { PROBLEM_TYPES } from '@rheora/enrollment-contract';

/**
 * E-SF18 — the logic behind the contract-review step, kept OUT of the `.tsx`
 * per CLAUDE.md. Pure: no fetches, no timers, no `Date.now()` unless injected.
 *
 * Three things live here, and they are all things a component would otherwise
 * inline as a chain of ternaries in JSX:
 *
 *   1. WHAT THE WAIT SHOULD SAY at a given elapsed time (honest progress).
 *   2. WHETHER A FAILURE IS WORTH RETRYING, and what to say about it.
 *   3. WHY THE PRIMARY ACTION IS UNAVAILABLE, as a single i18n key.
 */

/**
 * ── HOW THE BROWSER'S OWN PDF VIEWER SHOULD OPEN THE CONTRACT ──────────────
 *
 * PDF open parameters, appended to the object URL as a fragment. Measured in
 * Chrome at 1375px: without them the viewer opened the contract at **50% zoom
 * with the page-thumbnail rail expanded**, which spent ~40% of a 726px column on
 * navigation chrome and rendered the actual page about 360px wide — undoing most
 * of the width the layout had just bought back.
 *
 *   `view=FitH`      fit the page WIDTH to the frame. The contract is read
 *                    top-to-bottom in a tall column, so width is the dimension
 *                    that must not be sacrificed.
 *   `pagemode=none`  collapse the thumbnail rail. It is navigation for a document
 *                    the customer is meant to read straight through, and it costs
 *                    more width than it returns.
 *   `navpanes=0`     the Acrobat-era spelling of the same thing, for viewers that
 *                    honour it instead.
 *
 * ⚠️ THE TOOLBAR IS DELIBERATELY LEFT ON. `toolbar=0` would have been tidier and
 * would have removed the customer's own zoom, page count, print and download —
 * on a regulatory document they are about to execute. Chrome's viewer also treats
 * these as HINTS, not guarantees, which is exactly why the "Open your contract in
 * a new tab" affordance stays: a viewer that ignores all of this must still leave
 * the contract readable.
 *
 * A CONSTANT, not a template: nothing customer-supplied is interpolated into a URL
 * fragment here, and there is no reason for that ever to change.
 */
export const PDF_VIEWER_PARAMS = '#view=FitH&pagemode=none&navpanes=0';

/**
 * The object URL a PDF viewer should be pointed at, or `null` when there is none.
 *
 * Kept out of the `.tsx` per CLAUDE.md, and kept OUT of `useObjectUrl` on purpose:
 * that hook owns a RESOURCE LIFETIME (mint/revoke) and must hand back the exact
 * string it will later revoke. Revoking a URL with a fragment attached is not the
 * same call, so the display concern is composed on top rather than folded in.
 */
export function pdfViewerSrc(objectUrl: string | null): string | null {
  return objectUrl === null ? null : `${objectUrl}${PDF_VIEWER_PARAMS}`;
}

/**
 * Progress copy escalates on a schedule rather than sitting on one message.
 *
 * ⚠️ THE NUMBERS ARE NOT DECORATIVE. QA measured the SIGNED render at 279s cold /
 * 34s warm, and E41 is reducing that in parallel; the UNSIGNED preview this step
 * fetches is the cheaper path (E40 skips the blob upload and caches per pricing
 * snapshot) but is bounded at 10s upstream before it converts to a retryable 503.
 * So: a short first beat that most warm requests never outlive, a second beat that
 * names the wait honestly, and a third that tells the customer the render is close
 * to its budget and a retry is coming. A single unchanging "Loading…" over that
 * range is indistinguishable from a hung page — which is exactly the "indefinite
 * spinner" this epic forbids.
 */
export const PREVIEW_SLOW_AFTER_MS = 4_000;
export const PREVIEW_VERY_SLOW_AFTER_MS = 12_000;

/** i18n keys for the three progress beats, in escalation order. */
export type PreviewProgressKey =
  | 'shop.sign.document.loading.start'
  | 'shop.sign.document.loading.slow'
  | 'shop.sign.document.loading.verySlow';

/**
 * The progress message for a render that has been running `elapsedMs`.
 *
 * Monotonic by construction (the thresholds only ever escalate), so a customer
 * never sees the wait appear to get shorter — which reads as a bug even when the
 * underlying request is fine.
 */
export function previewProgressKey(elapsedMs: number): PreviewProgressKey {
  if (elapsedMs >= PREVIEW_VERY_SLOW_AFTER_MS) return 'shop.sign.document.loading.verySlow';
  if (elapsedMs >= PREVIEW_SLOW_AFTER_MS) return 'shop.sign.document.loading.slow';
  return 'shop.sign.document.loading.start';
}

/**
 * How a failed preview should be presented.
 *
 * `retryable`  — the render did not finish this time (BFF 503
 *                `agreement-preview-unavailable`, or a transport failure).
 *                Nothing is wrong with the enrollment; trying again genuinely
 *                works, because E40 may answer the next attempt from its warm
 *                per-snapshot cache.
 * `notReady`   — there is no DRAFT contract for this enrollment yet (BFF 409).
 *                A retry cannot help; the customer has to go back a step.
 * `sessionLost`— the funnel session expired (401). The only route forward is to
 *                start again from the address, exactly as everywhere else in this
 *                app (`isSessionExpired` in bff-client explains why we do NOT
 *                quietly mint a new one).
 * `unknown`    — anything else. Presented as a failure with a retry, because a
 *                retry is harmless here (the read has no side effects) and being
 *                stuck with no control at all is worse.
 */
export type PreviewFailureKind = 'retryable' | 'notReady' | 'sessionLost' | 'unknown';

export interface PreviewFailure {
  readonly kind: PreviewFailureKind;
  /** i18n key for the title of the failure notice. */
  readonly titleKey: string;
  /** i18n key for the body copy. */
  readonly bodyKey: string;
  /** Whether to offer the customer a "try again" control. */
  readonly canRetry: boolean;
}

/**
 * Classify a preview failure from the STATUS and the RFC7807 `type`.
 *
 * Takes the two primitives rather than a `BffError` so this stays a pure function
 * with no dependency on the API layer — the same split `quote-draft-error.service`
 * uses, and for the same reason: the classification is product policy, not
 * transport detail.
 */
export function classifyPreviewFailure(
  status: number | undefined,
  problemType: string | undefined,
): PreviewFailure {
  if (status === 401) {
    return {
      kind: 'sessionLost',
      titleKey: 'shop.sign.document.error.sessionLost.title',
      bodyKey: 'shop.sign.document.error.sessionLost.body',
      canRetry: false,
    };
  }
  if (problemType === PROBLEM_TYPES.agreementPreviewNotReady || status === 409) {
    return {
      kind: 'notReady',
      titleKey: 'shop.sign.document.error.notReady.title',
      bodyKey: 'shop.sign.document.error.notReady.body',
      canRetry: false,
    };
  }
  if (problemType === PROBLEM_TYPES.agreementPreviewUnavailable || status === 503) {
    return {
      kind: 'retryable',
      titleKey: 'shop.sign.document.error.retryable.title',
      bodyKey: 'shop.sign.document.error.retryable.body',
      canRetry: true,
    };
  }
  // Includes `status === undefined` — a transport failure, which is exactly the
  // case a retry most often fixes.
  return {
    kind: 'unknown',
    titleKey: 'shop.sign.document.error.unknown.title',
    bodyKey: 'shop.sign.document.error.unknown.body',
    canRetry: true,
  };
}

export interface SignGateInput {
  /**
   * TRUE once the contract PDF has actually been rendered on the page (US-SF18.3).
   *
   * ⚠️ "PRESENTED", NOT "READ". We can honestly assert that the document was put
   * in front of the customer; we cannot assert that they read it, and a UI that
   * claimed to measure reading would be lying about the strongest thing it says.
   * The acknowledgement checkboxes are what carry the customer's assertion — this
   * only guarantees they were not asked to make it against an unseen document.
   */
  readonly documentPresented: boolean;
  /** The SERVER-AUTHORITATIVE deposit gate (`isDepositGateSatisfied`). */
  readonly depositSatisfied: boolean;
  /**
   * A drawn mark is present and passed the client-side bounds (`hasDrawnSignature`).
   *
   * ⚠️ THIS IS A REVERSAL OF ADR-0010 D5.6 / E-SF17, MADE DELIBERATELY AND FROM
   * ABOVE — read this before "restoring" the old behaviour from git history.
   *
   * The drawn mark used to be optional, on the reasoning that the typed name plus
   * the three acknowledgements are the operative ESIGN mark and a canvas cannot be
   * operated without a pointer. Both halves of that reasoning are STILL TRUE: this
   * change does not make the drawing operative, and it does not relax anything the
   * typed name or the acknowledgements already require. It ADDS a business
   * requirement on top of them.
   *
   * ⚠️ AND IT HAS A REAL COST, WHICH MUST NOT BE QUIETLY FORGOTTEN. A customer who
   * cannot use a pointer — keyboard-only, screen-reader, some motor impairments —
   * can no longer complete this funnel at all. There is no honest client-side
   * accommodation available today: a "I can't draw" bypass that records nothing is
   * indistinguishable from the optionality it replaces, and synthesising a script
   * rendering of the typed name would fabricate a mark the customer never made on
   * a regulated document. The accommodation that WOULD be honest needs a recorded,
   * customer-elected field on `SignatureEventSchema` carried through the BFF to
   * Quotation — a cross-repo change. See the sign-step epic note.
   */
  readonly signatureDrawn: boolean;
  /** A submit is already in flight. */
  readonly submitting: boolean;
}

/**
 * The i18n key explaining why signing is unavailable, or `undefined` when it is
 * available.
 *
 * ⚠️ ORDER MATTERS AND IS NOT ARBITRARY. The deposit is reported first because it
 * is the harder blocker — a customer who has not settled a required deposit cannot
 * proceed even with the document in front of them, and telling them to "wait for
 * the contract" would send them to watch a spinner instead of paying. One reason at
 * a time: a stack of simultaneous refusals is how a customer decides the page is
 * broken.
 *
 * ⚠️ THE CONSENT CHECKBOXES ARE DELIBERATELY NOT IN THIS LIST. Missing consents
 * leave the primary action LIVE, so pressing it surfaces a per-checkbox error
 * pointing at the box that is missing. Folding them in here would replace three
 * precise, adjacent messages with one vague one — and would put the §25.475
 * acceptances behind an inert control rather than in front of a correction.
 */
export function signBlockedReasonKey(input: SignGateInput): string | undefined {
  if (input.submitting) return undefined;
  if (!input.depositSatisfied) return 'shop.sign.submit.depositBlocked';
  if (!input.documentPresented) return 'shop.sign.submit.documentBlocked';
  // LAST of the three, and the ordering is the same rule as above: the deposit and
  // the contract render both send the customer somewhere ELSE, so they must be
  // cleared first. The drawing is the one thing they do inside this panel, and it
  // is pointless to ask for it while the panel is still waiting on the document
  // the mark is supposed to accompany.
  if (!input.signatureDrawn) return 'shop.sign.submit.signatureBlocked';
  return undefined;
}

/**
 * Whether the primary sign control is actionable.
 *
 * Kept separate from `signBlockedReasonKey` so the button's `disabled` and the
 * sentence next to it are derived from ONE source rather than from two ternaries
 * that can drift apart. QA found a primary button reporting `disabled === false`
 * to assistive technology while being inert; the fix is not a nicer style, it is
 * that "can I press this?" and "why not?" must be the same computation.
 */
export function isSignActionable(input: SignGateInput): boolean {
  return !input.submitting && signBlockedReasonKey(input) === undefined;
}
